import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchUsers();
  }, [page, filter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin/users?page=${page}&limit=20&role=${filter === 'all' ? '' : filter}`);
      setUsers(res.data.data || []);
      setTotal(res.data.count || 0);
    } catch {
      // Mock data
      setUsers([
        { _id: '1', name: 'Rahul Deshmukh', email: 'rahul@example.com', phone: '9876543210', role: 'user', isActive: true, createdAt: new Date().toISOString(), totalBookings: 12 },
        { _id: '2', name: 'Priya Kulkarni', email: 'priya@example.com', phone: '9876543211', role: 'user', isActive: true, createdAt: new Date().toISOString(), totalBookings: 8 },
        { _id: '3', name: 'Admin User', email: 'admin@smartpark.com', phone: '9876543212', role: 'admin', isActive: true, createdAt: new Date().toISOString(), totalBookings: 0 },
        { _id: '4', name: 'Amit Jadhav', email: 'amit@example.com', phone: '9876543213', role: 'user', isActive: false, createdAt: new Date().toISOString(), totalBookings: 3 },
        { _id: '5', name: 'Sneha Patil', email: 'sneha@example.com', phone: '9876543214', role: 'user', isActive: true, createdAt: new Date().toISOString(), totalBookings: 21 },
      ]);
      setTotal(5);
    } finally {
      setLoading(false);
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/admin/users/${userId}/toggle-status`);
      setUsers(users.map(u => u._id === userId ? { ...u, isActive: !currentStatus } : u));
      toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
    } catch {
      toast.error('Failed to update user status');
    }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole });
      setUsers(users.map(u => u._id === userId ? { ...u, role: newRole } : u));
      toast.success('Role updated');
    } catch {
      toast.error('Failed to update role');
    }
  };

  const filtered = users.filter(u =>
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    u.phone?.includes(search)
  );

  const inputStyle = {
    padding: '10px 14px',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '10px', color: '#fff', fontSize: '14px', outline: 'none',
  };

  return (
    <div style={{ paddingTop: '64px', minHeight: '100vh' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
              <Link to="/admin" style={{ color: '#6366f1', fontSize: '13px', textDecoration: 'none' }}>← Admin</Link>
              <span style={{ color: '#374151' }}>/</span>
              <span style={{ fontSize: '13px', color: '#94a3b8' }}>Users</span>
            </div>
            <h1 style={{ fontSize: '26px', fontWeight: '900' }}>Manage Users</h1>
            <p style={{ color: '#64748b', fontSize: '14px', marginTop: '4px' }}>{total} total users</p>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Search by name, email, phone..."
            style={{ ...inputStyle, flex: 1, minWidth: '240px' }}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            {['all', 'user', 'admin'].map(r => (
              <button
                key={r}
                onClick={() => { setFilter(r); setPage(1); }}
                style={{
                  padding: '10px 18px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                  background: filter === r ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${filter === r ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.1)'}`,
                  color: filter === r ? '#6366f1' : '#94a3b8',
                }}
              >
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px', marginBottom: '24px' }}>
          {[
            { label: 'Total Users', value: total, color: '#6366f1' },
            { label: 'Active Users', value: users.filter(u => u.isActive).length, color: '#10b981' },
            { label: 'Admins', value: users.filter(u => u.role === 'admin').length, color: '#8b5cf6' },
            { label: 'Inactive', value: users.filter(u => !u.isActive).length, color: '#ef4444' },
          ].map((s, i) => (
            <div key={i} style={{
              background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '14px', padding: '16px',
            }}>
              <div style={{ fontSize: '22px', fontWeight: '900', color: s.color }}>{s.value}</div>
              <div style={{ color: '#64748b', fontSize: '12px', marginTop: '4px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Users Table */}
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                  {['User', 'Contact', 'Role', 'Bookings', 'Joined', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '14px 16px', color: '#64748b', fontSize: '12px', fontWeight: '700', borderBottom: '1px solid rgba(255,255,255,0.06)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading users...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No users found</td></tr>
                ) : (
                  filtered.map((u) => (
                    <motion.tr
                      key={u._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                    >
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: u.role === 'admin' ? 'linear-gradient(135deg, #8b5cf6, #6366f1)' : 'linear-gradient(135deg, #6366f1, #3b82f6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: '800', fontSize: '14px', flexShrink: 0,
                          }}>
                            {u.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: '700', fontSize: '14px' }}>{u.name}</div>
                            <div style={{ color: '#64748b', fontSize: '11px' }}>ID: {u._id.slice(-6)}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontSize: '13px', marginBottom: '2px' }}>{u.email}</div>
                        <div style={{ color: '#64748b', fontSize: '12px' }}>{u.phone}</div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <select
                          value={u.role}
                          onChange={e => changeRole(u._id, e.target.value)}
                          style={{
                            background: u.role === 'admin' ? 'rgba(139,92,246,0.15)' : 'rgba(99,102,241,0.1)',
                            border: `1px solid ${u.role === 'admin' ? 'rgba(139,92,246,0.4)' : 'rgba(99,102,241,0.3)'}`,
                            borderRadius: '8px', padding: '4px 10px',
                            color: u.role === 'admin' ? '#c4b5fd' : '#a5b4fc',
                            fontSize: '12px', fontWeight: '700', cursor: 'pointer', outline: 'none',
                          }}
                        >
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td style={{ padding: '14px 16px', fontWeight: '700', color: '#6366f1', fontSize: '15px' }}>
                        {u.totalBookings || 0}
                      </td>
                      <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '13px' }}>
                        {new Date(u.createdAt).toLocaleDateString('en-IN')}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          fontSize: '11px', fontWeight: '700',
                          color: u.isActive ? '#10b981' : '#ef4444',
                          background: u.isActive ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                          borderRadius: '6px', padding: '3px 8px',
                        }}>
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <button
                          onClick={() => toggleUserStatus(u._id, u.isActive)}
                          style={{
                            padding: '6px 14px',
                            background: u.isActive ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)',
                            border: `1px solid ${u.isActive ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)'}`,
                            borderRadius: '8px',
                            color: u.isActive ? '#ef4444' : '#10b981',
                            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                          }}
                        >
                          {u.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > 20 && (
            <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#64748b', fontSize: '13px' }}>
                Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} of {total}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{
                    padding: '6px 14px', background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
                    color: page === 1 ? '#374151' : '#94a3b8', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: '13px',
                  }}
                >← Prev</button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page * 20 >= total}
                  style={{
                    padding: '6px 14px', background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
                    color: page * 20 >= total ? '#374151' : '#94a3b8', cursor: page * 20 >= total ? 'not-allowed' : 'pointer', fontSize: '13px',
                  }}
                >Next →</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}